// frontend/src/utils/alertUtils.js

/**
 * Asks the browser for notification permission (must run from a user gesture, e.g. button click).
 * Returns { granted, state } where state is 'granted' | 'denied' | 'default' | 'unsupported'.
 *
 * If state was already "denied", most browsers will NOT show the prompt again — the user must
 * reset the site in browser settings. We still call requestPermission() so state can sync.
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support desktop notification');
    return { granted: false, state: 'unsupported' };
  }
  if (Notification.permission === 'granted') {
    return { granted: true, state: 'granted' };
  }
  // Always call on user gesture: 'default' → may show prompt; 'denied' → usually stays denied
  const result = await Notification.requestPermission();
  const state = result === 'default' || result === 'denied' || result === 'granted' ? result : 'denied';
  return { granted: result === 'granted', state };
};

export const showToastNotification = (title, options = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  try {
    new Notification(title, options);
  } catch (e) {
    console.error('Error showing notification', e);
  }
};

// ---------------- Sound presets ----------------
// We generate sounds via WebAudio for reliability (no network, no user-gesture
// race with remote assets). Three distinct alert characters are provided.

export const SOUND_PRESETS = [
  { id: 'chime', label: 'Chime',  description: 'Soft two-note ping' },
  { id: 'ding',  label: 'Ding',   description: 'Crisp bell' },
  { id: 'alert', label: 'Alert',  description: 'Urgent triple beep' },
];

let audioContext = null;

const ensureAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
};

const playTone = (ctx, { freqStart, freqEnd, duration, type = 'sine', volume = 0.35, delay = 0 }) => {
  const start = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = type;
  osc.frequency.setValueAtTime(freqStart, start);
  if (freqEnd && freqEnd !== freqStart) {
    osc.frequency.exponentialRampToValueAtTime(freqEnd, start + duration);
  }
  gain.gain.setValueAtTime(volume, start);
  gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
  osc.start(start);
  osc.stop(start + duration + 0.02);
};

const playChime = (ctx) => {
  playTone(ctx, { freqStart: 880, freqEnd: 660, duration: 0.25, type: 'sine',     volume: 0.35, delay: 0.00 });
  playTone(ctx, { freqStart: 660, freqEnd: 520, duration: 0.30, type: 'sine',     volume: 0.30, delay: 0.14 });
};

const playDing = (ctx) => {
  playTone(ctx, { freqStart: 1400, freqEnd: 900, duration: 0.45, type: 'triangle', volume: 0.40 });
};

const playAlertUrgent = (ctx) => {
  playTone(ctx, { freqStart: 1000, freqEnd: 1000, duration: 0.12, type: 'square', volume: 0.30, delay: 0.00 });
  playTone(ctx, { freqStart: 1000, freqEnd: 1000, duration: 0.12, type: 'square', volume: 0.30, delay: 0.18 });
  playTone(ctx, { freqStart: 1200, freqEnd: 1200, duration: 0.18, type: 'square', volume: 0.35, delay: 0.36 });
};

export const playAlertSound = (soundId = 'chime') => {
  try {
    const ctx = ensureAudioContext();
    switch (soundId) {
      case 'ding':  return playDing(ctx);
      case 'alert': return playAlertUrgent(ctx);
      case 'chime':
      default:      return playChime(ctx);
    }
  } catch (e) {
    console.error('Error playing alert sound', e);
  }
};
